import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { categories as mockCategories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || '';

  const [selectedCategory, setSelectedCategory] = useState(activeCat);

  useEffect(() => {
    if (activeCat !== selectedCategory) {
      setSelectedCategory(activeCat);
    }
  }, [activeCat]);

  const handleCategoryChange = (slug: string) => {
    setSelectedCategory(slug);
    setSearchParams(slug ? { cat: slug } : {});
  };

  const filteredArticles = useMemo(() => {
    if (!selectedCategory) return articles;
    return articles.filter((a) => a.categorySlug === selectedCategory);
  }, [selectedCategory]);

  const currentCategoryName = selectedCategory
    ? mockCategories.find((c) => c.slug === selectedCategory)?.name || ''
    : '';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: currentCategoryName ? `${currentCategoryName} - 男同网` : '全部分类 - 男同网',
            description: `浏览男同网的${currentCategoryName || '全部'}文章内容，涵盖交友互动、生活资讯、权益支持、真实故事、活动资讯和健康专栏。`,
            url: `https://mylifeguide.github.io/categories${selectedCategory ? `?cat=${selectedCategory}` : ''}`,
            inLanguage: 'zh-CN',
          }),
        }}
      />

      <article className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-20 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: currentCategoryName || '分类浏览' },
            ]}
          />

          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-2">
              {currentCategoryName || '分类浏览'}
            </h1>
            <p className="text-foreground-500 text-sm">
              {currentCategoryName
                ? `共 ${filteredArticles.length} 篇${currentCategoryName}相关文章`
                : `共 ${articles.length} 篇文章，选择分类开始浏览`}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            <button
              onClick={() => handleCategoryChange('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                !selectedCategory
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
              }`}
            >
              全部
            </button>
            {mockCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                  selectedCategory === cat.slug
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border-background-200/70'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-medium text-foreground-700">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span>{article.readTime} 分钟阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
            </div>
          )}
        </div>
      </article>
    </>
  );
}